/**
 * Phase 8 T-17 §23.B — Toast variant component.
 *
 * Four variants with exact hex values from the §23.B table:
 *
 * | Variant | Icon          | Bg                     | Text      | Border   |
 * |---------|---------------|------------------------|-----------|----------|
 * | success | CheckCircle2  | rgba(16,185,129,.10)   | #065F46   | #A7F3D0  |
 * | info    | Info          | rgba(59,130,246,.10)   | #1E3A8A   | #BFDBFE  |
 * | warning | AlertTriangle | rgba(245,158,11,.10)   | #78350F   | #FDE68A  |
 * | error   | XCircle       | rgba(239,68,68,.10)    | #7F1D1D   | #FCA5A5  |
 *
 * A11y: role="status" on success/info, role="alert" on warning/error.
 * Dismiss button is always visible (top-right).
 */

import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';

export type ToastVariant = 'success' | 'info' | 'warning' | 'error';

export interface ToastItem {
  id: number;
  title: string;
  body?: string;
  variant: ToastVariant;
}

const VARIANT_CONFIG = {
  success: {
    bg: 'rgba(16,185,129,.10)',
    text: '#065F46',
    border: '#A7F3D0',
    role: 'status' as const,
    Icon: CheckCircle2,
  },
  info: {
    bg: 'rgba(59,130,246,.10)',
    text: '#1E3A8A',
    border: '#BFDBFE',
    role: 'status' as const,
    Icon: Info,
  },
  warning: {
    bg: 'rgba(245,158,11,.10)',
    text: '#78350F',
    border: '#FDE68A',
    role: 'alert' as const,
    Icon: AlertTriangle,
  },
  error: {
    bg: 'rgba(239,68,68,.10)',
    text: '#7F1D1D',
    border: '#FCA5A5',
    role: 'alert' as const,
    Icon: XCircle,
  },
} as const;

interface ToastProps {
  item: ToastItem;
  onDismiss: (id: number) => void;
}

export function Toast({ item, onDismiss }: ToastProps) {
  const cfg = VARIANT_CONFIG[item.variant];
  const { Icon } = cfg;

  return (
    <div
      role={cfg.role}
      aria-live={cfg.role === 'alert' ? 'assertive' : 'polite'}
      aria-atomic="true"
      className="animate-fade-in-up flex w-[min(calc(100vw-2rem),360px)] items-start gap-3 rounded-[12px] border px-4 py-3 shadow-lg"
      style={{
        background: cfg.bg,
        borderColor: cfg.border,
        color: cfg.text,
      }}
    >
      <Icon
        className="mt-0.5 h-4 w-4 flex-shrink-0"
        aria-hidden
        style={{ color: cfg.text }}
      />

      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold leading-snug" style={{ color: cfg.text }}>
          {item.title}
        </p>
        {item.body ? (
          <p
            className="mt-0.5 text-[12px] leading-snug opacity-80"
            style={{ color: cfg.text }}
          >
            {item.body}
          </p>
        ) : null}
      </div>

      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={() => onDismiss(item.id)}
        className="mt-0.5 flex-shrink-0 rounded-[6px] p-0.5 opacity-60 transition-opacity hover:opacity-100"
        style={{ color: cfg.text }}
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}
